import {
  adminApproveBooking,
  adminApproveContract,
  adminSelfApproveBooking,
  adminSelfApproveContract,
  bookAvailablity,
  IAdminApproveDetailsData,
  IAdminRequestDetailsContractModel,
  selfBookAvailablity,
} from '@pharmaplan/common';

import strings from '../../localization';
import type { AppDispatch } from '../../store';

interface IUseApproveBookingDerivedValues {
  bookingId: string;
  details?: IAdminApproveDetailsData | IAdminRequestDetailsContractModel;
  dispatch: AppDispatch;
  isSelfService: boolean;
  longTermShiftContractId?: string;
  multiCreditsLeft?: number;
  multiProfessionalCreditsLeft?: number;
  singleCreditsLeft?: number;
  singleProfessionalCreditsLeft?: number;
  workshiftId: string;
}

const useApproveBookingDerivedValues = ({
  bookingId,
  details,
  dispatch,
  isSelfService,
  longTermShiftContractId,
  multiCreditsLeft,
  multiProfessionalCreditsLeft,
  singleCreditsLeft,
  singleProfessionalCreditsLeft,
  workshiftId,
}: IUseApproveBookingDerivedValues) => {
  const approveApis = isSelfService
    ? {
      approveContract: adminSelfApproveContract,
      approveBooking: adminSelfApproveBooking,
      approveAvailability: selfBookAvailablity,
    }
    : {
      approveBooking: adminApproveBooking,
      approveContract: adminApproveContract,
      approveAvailability: bookAvailablity,
    };

  const getCreditValues = () => {
    const multiHourlyBonus = details?.professionalCreditsLeft
      ? (details as IAdminRequestDetailsContractModel)?.hourlyRateBonus ?? 0
      : 0;
    const singleHourlyBonus = details?.professionalCreditsLeft
      ? (details as IAdminApproveDetailsData)?.hourlyRateBonus ?? 0
      : 0;

    const { creditsLeft, professionalCreditsLeft, hourlyBonus } = longTermShiftContractId
      ? {
        creditsLeft: multiCreditsLeft,
        professionalCreditsLeft: multiProfessionalCreditsLeft,
        hourlyBonus: multiHourlyBonus,
      }
      : {
        creditsLeft: singleCreditsLeft,
        professionalCreditsLeft: singleProfessionalCreditsLeft,
        hourlyBonus: singleHourlyBonus,
      };

    return { multiHourlyBonus, singleHourlyBonus, creditsLeft, professionalCreditsLeft, hourlyBonus };
  };

  const { singleHourlyBonus, multiHourlyBonus, creditsLeft, professionalCreditsLeft, hourlyBonus } = getCreditValues();

  const {
    mealAllowance,
    accommodationAllowance,
    pharmacistHourlyRate: detailsPharmacistHourlyRate,
    travelAllowanceAmount,
    travelAllowanceRatePerKm,
    counterAccommodationAllowance,
    counterMealAllowance,
    counterTravelAllowanceAmount,
    counterTravelAllowanceRatePerKm,
    counterHourlyRate,
    counterEmergencyFees,
    counterOtherAllowance,
    pharmacyHourlyRate: detailsPharmacyHourlyRate,
    travelAllowance,
    promoHourlyRate: singlePromoHourlyRate,
    emergencyFees,
    otherAllowance,
  } = (details as IAdminApproveDetailsData) ?? {};

  const {
    longTermShiftContract,
    counterShiftContract,
    promoHourlyRate: multiPromoHourlyRate,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
  } = details as IAdminRequestDetailsContractModel;

  const {
    weekDayPaidHourlyRate: counterWeekday,
    weekendPaidHourlyRate: counterWeekend,
  } = counterShiftContract ?? {};

  const {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    pharmacyWeekDayPaidHourlyRate,
    pharmacyWeekendPaidHourlyRate,
  } = longTermShiftContract ?? {};

  const pharmacyWeekday = pharmacyWeekDayPaidHourlyRate || pharmacyWeekDayHourlyRate;
  const pharmacyWeekend = pharmacyWeekendPaidHourlyRate || pharmacyWeekendHourlyRate;

  const getApproveData = () => {
    const approveData = {
      travelAllowance,
      mealAllowance: counterMealAllowance || mealAllowance,
      accommodationAllowance:
      counterAccommodationAllowance || accommodationAllowance,
      travelAllowanceAmount:
      counterTravelAllowanceAmount || travelAllowanceAmount,
      travelAllowanceRatePerKm:
      counterTravelAllowanceRatePerKm || travelAllowanceRatePerKm,

      pharmacistHourlyRate:
      counterHourlyRate
      || Number(detailsPharmacistHourlyRate) + Number(singleHourlyBonus),
      emergencyFees: counterEmergencyFees || emergencyFees,
      pharmacyHourlyRate: singleCreditsLeft
        ? singlePromoHourlyRate
        : detailsPharmacyHourlyRate,
      otherAllowance: counterOtherAllowance || otherAllowance,
      weekDayPaidHourlyRate: counterWeekday || weekDayPaidHourlyRate,
      weekendPaidHourlyRate: counterWeekend || weekendPaidHourlyRate,
      pharmacyWeekDayHourlyRate: pharmacyWeekday,
      pharmacyWeekendHourlyRate: pharmacyWeekend,
      promoHourlyRate: multiPromoHourlyRate,
      hourlyRateBonus: multiHourlyBonus,
    };
    return approveData;
  };

  const getInfoText = () => {
    const pharmacyInfoText = creditsLeft
      ? (strings.formatString(
        strings.promotionalCreditWillBeApplied,
        `${creditsLeft}`,
      ) as string)
      : '';

    const professionalInfoText = professionalCreditsLeft
      ? (strings.formatString(
        strings.promotionalBonusWillBeApplied,
        `${hourlyBonus}`,
        `${professionalCreditsLeft}`,
      ) as string)
      : '';

    return { pharmacyInfoText, professionalInfoText };
  };

  const submitBookingApproval = (
    isBookAvailability: boolean,
    formikValues: any,
  ) => {
    if (isBookAvailability) {
      dispatch(
        approveApis.approveAvailability({
          ...formikValues,
          workshiftId,
          availabilityId: bookingId,
        }),
      );
    } else {
      dispatch(approveApis.approveBooking({ ...formikValues }));
    }
  };

  return {
    approveContract: approveApis.approveContract,
    ...getCreditValues(),
    ...getInfoText(),
    approveData: getApproveData(),
    submitBookingApproval,
  };
};

export default useApproveBookingDerivedValues;
